import { useEffect, useRef } from 'react';
import { CircularProgress, Stack } from '@mui/material';
import {
    useGetUserInfoQuery,
    useGetSettingsQuery,
    useGetSessionTimestampQuery,
    useReloadSessionMutation,
} from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import InstallPage from '@/pages/InstallPage';
import Login from '@/Login';
import Router from '@/Router';
import DebugScreenshotButton from '@/components/DebugScreenshotButton';

const App = () => {
    const { setLocale, locale } = useI18nContext();
    const categoriesUpdatedRef = useRef<string | null | undefined>(undefined);
    const indexUpdatedRef = useRef<string | null | undefined>(undefined);
    const [reloadSession] = useReloadSessionMutation();

    // Check installation status and authentication via Settings endpoint
    const { data: settings, isLoading: settingsLoading } = useGetSettingsQuery();

    const {
        data: userInfo,
        isLoading: userLoading,
        error: userError,
    } = useGetUserInfoQuery(undefined, {
        skip: !settings?.installed || !settings?.authenticated,
    });

    // Poll Session endpoint for timestamp changes (bypasses UserInfo cache)
    const {
        data: sessionData,
        error: sessionError,
        isError: isSessionError,
    } = useGetSessionTimestampQuery(undefined, {
        skip: !settings?.installed || !settings?.authenticated || !userInfo,
        pollingInterval: 15000, // Poll every 15 seconds to check for category assignment changes
    });

    // If the backend returns 401 OR indicates unauthenticated in the payload, force user back to login.
    const isSessionUnauthorized =
        (isSessionError &&
            !!sessionError &&
            typeof sessionError === 'object' &&
            'status' in sessionError &&
            (sessionError as { status?: number | string }).status === 401) ||
        sessionData?.unauthenticated === true;

    // ALL useEffect hooks MUST come before any conditional returns
    useEffect(() => {
        document.title = `${location.host} - Plan`;
    }, [location.host]);

    useEffect(() => {
        const lang = locale.trim();
        if (lang) {
            document.documentElement.lang = lang;
        }
    }, [locale]);

    // Set initial language from user preference or system default
    useEffect(() => {
        if (userInfo?.language) {
            setLocale(userInfo.language as 'en' | 'de');
        } else if (settings?.defaultLanguage) {
            setLocale(settings.defaultLanguage as 'en' | 'de');
        }
    }, [userInfo?.language, settings?.defaultLanguage, setLocale]);

    // Check for category assignment changes and reload session data
    useEffect(() => {
        if (!userInfo || !sessionData) {
            return;
        }

        const currentTimestamp = sessionData.categoriesUpdated;
        const currentIndexTimestamp = sessionData.indexUpdated ?? null;

        // First time - store the initial value
        if (categoriesUpdatedRef.current === undefined) {
            categoriesUpdatedRef.current = currentTimestamp ?? null;
            // store initial index.html timestamp on first poll as well
            indexUpdatedRef.current = currentIndexTimestamp;
            return;
        }

        // Check if categories have been updated
        if (categoriesUpdatedRef.current !== currentTimestamp) {
            // Update the ref to prevent repeated reloads
            categoriesUpdatedRef.current = currentTimestamp;

            // Reload session data from database (this will also invalidate caches)
            reloadSession()
                .unwrap()
                .then(() => {
                    console.log('Session reloaded successfully after changes.');
                })
                .catch((error) => {
                    console.error('Failed to reload session:', error);
                });
        }

        // Check if the index.html has been updated on disk. If so, trigger a full page reload
        // so the client picks up new static assets. Only reload once per detected change.
        if (
            indexUpdatedRef.current !== undefined &&
            indexUpdatedRef.current !== currentIndexTimestamp &&
            currentIndexTimestamp !== null
        ) {
            console.log(
                'index.html update detected on server; reloading page to pick up new assets.'
            );
            // prevent further reload attempts
            indexUpdatedRef.current = currentIndexTimestamp;
            // Hard reload the page (bypass cache) to ensure new index and assets are fetched
            window.location.reload();
        }
    }, [sessionData?.categoriesUpdated, sessionData, userInfo, reloadSession]);

    // If the periodic /Session polling tells us the session is gone, force user back to login.
    useEffect(() => {
        if (!isSessionUnauthorized) {
            return;
        }

        // Clear the ref so a new login doesn't immediately trigger a reload loop.
        categoriesUpdatedRef.current = undefined;
    }, [isSessionUnauthorized]);

    if (settingsLoading || userLoading) {
        return (
            <Stack sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    // If installation required, always show install page
    if (settings && !settings.installed) {
        return <InstallPage />;
    }

    if (!userInfo || userError || isSessionUnauthorized) {
        return <Login />;
    }

    return (
        <>
            <Router admin={userInfo.admin} />
            {userInfo.debug && <DebugScreenshotButton />}
        </>
    );
};

export default App;
